import { BASE_URL } from '../components/AppConfig';
import { getBearerToken } from '../utils/authToken';

const authFetch = async (path, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...(optionHeaders || {}),
  };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
    });
    const json = await res.json().catch(() => ({}));
    if (!json.status_code && !json.statusCode) {
      if (res.status === 409) {
        json.status_code = '10001';
        json.message = json.message || 'Options cannot be edited after the first submission.';
      } else if (res.status === 400) {
        json.status_code = '10001';
        json.message = json.message || 'Invalid Pulse request.';
      } else if (res.status === 404) {
        json.status_code = '10001';
        json.message = json.message || 'Pulse card not found.';
      } else if (res.status === 402) {
        json.status_code = '10001';
        json.message = json.message || 'Insufficient Fan Energy for this entry fee.';
      }
    }
    json._http_status = res.status;
    return json;
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

const authMultipartFetch = async (path, formData, options = {}) => {
  const token = getBearerToken();
  const { headers: optionHeaders, ...rest } = options;
  const headers = { ...(optionHeaders || {}) };
  if (token) {
    headers.Authorization = token;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      credentials: 'include',
      ...rest,
      headers,
      body: formData,
    });
    const json = await res.json().catch(() => ({}));
    if (!json.status_code && !json.statusCode && res.ok) {
      json.status_code = '10000';
    }
    if (!json.status_code && !json.statusCode && !res.ok) {
      json.status_code = '10001';
      json.message = json.message || 'Upload failed.';
    }
    json._http_status = res.status;
    return json;
  } catch {
    return { statusCode: '50000', message: 'Request failed' };
  }
};

export const isPulseApiSuccess = (res) =>
  String(res?.statusCode || res?.status_code || '') === '10000';

export const getPulsePayload = (res) => res?.data ?? null;

export const isPulseAuthError = (res) => {
  const code = String(res?.statusCode || res?.status_code || '');
  const message = String(res?.message || '').toLowerCase();
  if (code === '10003') return true;
  return (
    code === '10001' &&
    (message.includes('token') ||
      message.includes('jwt') ||
      message.includes('credential') ||
      message.includes('authenticated'))
  );
};

export const isPulseOptionsLockedError = (res) => {
  const status = res?._http_status;
  const message = String(res?.message || '').toLowerCase();
  return status === 409 || (message.includes('options') && message.includes('lock'));
};

export const getPulseErrorMessage = (res, fallback) => {
  if (isPulseAuthError(res)) {
    return 'Session token is invalid or expired. Log out, log in again, then reopen this page.';
  }
  return String(res?.message || '') || fallback;
};

/** GET /admin/pulse/matches */
export const listPulseMatches = () =>
  authFetch('/admin/pulse/matches', { method: 'GET' });

/** PATCH /admin/pulse/matches/:matchId */
export const patchPulseMatch = (matchId, body) =>
  authFetch(`/admin/pulse/matches/${matchId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** GET /admin/pulse/competitions */
export const listPulseCompetitions = () =>
  authFetch('/admin/pulse/competitions', { method: 'GET' });

/** PATCH /admin/pulse/competitions/:cid */
export const patchPulseCompetition = (cid, body) =>
  authFetch(`/admin/pulse/competitions/${cid}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** GET /admin/pulse/cards */
export const listPulseCards = (filters = {}) => {
  const params = new URLSearchParams();
  const limit = Math.min(100, Math.max(1, Number(filters.limit) || 30));
  params.set('limit', String(limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  if (filters.status) params.set('status', filters.status);
  if (filters.category) params.set('category', filters.category);
  if (filters.categories) params.set('categories', filters.categories);
  if (filters.deck) params.set('deck', filters.deck);
  if (filters.match_id) params.set('match_id', String(filters.match_id));
  if (filters.search) params.set('search', filters.search);
  // order=app (default) = live deck first, same cadence as the app. order=updated = archive browse.
  if (filters.order) params.set('order', filters.order);
  return authFetch(`/admin/pulse/cards?${params.toString()}`, { method: 'GET' });
};

/** GET /admin/pulse/cards/summary — header counts */
export const getPulseCardsSummary = () =>
  authFetch('/admin/pulse/cards/summary', { method: 'GET' });

/** GET /admin/pulse/deck — live app-order deck (all types, same as a fresh app open) */
export const listPulseDeck = (filters = {}) => {
  const params = new URLSearchParams();
  const limit = Math.min(100, Math.max(1, Number(filters.limit) || 50));
  params.set('limit', String(limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  return authFetch(`/admin/pulse/deck?${params.toString()}`, { method: 'GET' });
};

/** POST /admin/pulse/upload-image */
export const uploadPulseImage = (imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  return authMultipartFetch('/admin/pulse/upload-image', formData, { method: 'POST' });
};

/** PATCH /admin/pulse/cards/:id with multipart image field */
export const patchPulseCardImage = (id, imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  return authMultipartFetch(`/admin/pulse/cards/${encodeURIComponent(id)}`, formData, {
    method: 'PATCH',
  });
};

export const getPulseUploadedImageUrl = (res) => {
  const data = getPulsePayload(res) || res || {};
  return data.image_url || data.imageUrl || null;
};

/** POST /admin/pulse/cards — daily knowledge */
export const createPulseCard = (body) =>
  authFetch('/admin/pulse/cards', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** GET /admin/pulse/hot-takes — standalone admin opinion cards, newest first */
export const listPulseHotTakes = (filters = {}) => {
  const params = new URLSearchParams();
  const limit = Math.min(100, Math.max(1, Number(filters.limit) || 30));
  params.set('limit', String(limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  if (filters.status) params.set('status', filters.status);
  return authFetch(`/admin/pulse/hot-takes?${params.toString()}`, { method: 'GET' });
};

/** POST /admin/pulse/hot-takes — source_type hot_take_admin */
export const createPulseHotTake = (body) =>
  authFetch('/admin/pulse/hot-takes', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** GET /admin/pulse/analytics — date + category applied together */
export const getPulseAnalytics = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (filters.category) params.set('category', filters.category);
  if (filters.categories) params.set('categories', filters.categories);
  const qs = params.toString();
  return authFetch(`/admin/pulse/analytics${qs ? `?${qs}` : ''}`, { method: 'GET' });
};

const PULSE_ANALYTICS_SORTS = new Set([
  'created_at',
  'expires_at',
  'submissions',
  'unique_submissions',
  'unique_views',
  'views',
  'skips',
  'awarded',
  'fe_awarded',
  'fe_pending',
  'fe_spent',
  'interaction_rate',
]);

function appendAnalyticsSort(params, filters) {
  const sort = PULSE_ANALYTICS_SORTS.has(filters.sort) ? filters.sort : 'created_at';
  const order = filters.order === 'asc' ? 'asc' : 'desc';
  params.set('sort', sort);
  params.set('order', order);
}

/** GET /admin/pulse/analytics/cards — same date + category filters as /analytics. sort/order apply to the full set. */
export const getPulseAnalyticsCards = (filters = {}) => {
  const params = new URLSearchParams();
  const limit = Math.min(100, Math.max(1, Number(filters.limit) || 30));
  params.set('limit', String(limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  if (filters.category) params.set('category', filters.category);
  if (filters.categories) params.set('categories', filters.categories);
  if (filters.match_id) params.set('match_id', String(filters.match_id));
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  appendAnalyticsSort(params, filters);
  return authFetch(`/admin/pulse/analytics/cards?${params.toString()}`, { method: 'GET' });
};

function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
  } catch {
    return match[1].replace(/"/g, '').trim();
  }
}

/**
 * GET /admin/pulse/analytics/cards/export — full .xlsx, not the current page.
 * Same date/category/match filters and the same sort/order as the table. Do not send limit or offset.
 */
export const exportPulseAnalyticsCards = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.set('category', filters.category);
  if (filters.categories) params.set('categories', filters.categories);
  if (filters.match_id) params.set('match_id', String(filters.match_id));
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  appendAnalyticsSort(params, filters);

  const token = getBearerToken();
  const headers = {};
  if (token) headers.Authorization = token;

  const qs = params.toString();
  const res = await fetch(
    `${BASE_URL}/admin/pulse/analytics/cards/export${qs ? `?${qs}` : ''}`,
    { method: 'GET', credentials: 'include', headers }
  );

  const contentType = res.headers.get('Content-Type') || '';
  const disposition = res.headers.get('Content-Disposition') || '';
  const looksLikeSheet =
    contentType.includes('spreadsheet') ||
    contentType.includes('octet-stream') ||
    contentType.includes('excel') ||
    /\.xlsx/i.test(disposition);

  if (!res.ok || contentType.includes('json') || !looksLikeSheet) {
    const json = await res.json().catch(() => ({}));
    const message = json.message || `Export failed (${res.status}).`;
    const error = new Error(message);
    error.response = json;
    throw error;
  }

  const blob = await res.blob();
  const from = filters.date_from || 'all';
  const to = filters.date_to || 'all';
  const fallback = `pulse-card-performance_${from}_${to}.xlsx`;
  const filename = filenameFromDisposition(res.headers.get('Content-Disposition'), fallback);
  return { blob, filename };
};

export const triggerPulseBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** GET /admin/pulse/cards/:id */
export const getPulseCard = (id) =>
  authFetch(`/admin/pulse/cards/${encodeURIComponent(id)}`, { method: 'GET' });

/** PATCH /admin/pulse/cards/:id */
export const patchPulseCard = (id, body) =>
  authFetch(`/admin/pulse/cards/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

/** POST /admin/pulse/cards/:id/regenerate — news / fan_story AI fields */
export const regeneratePulseCard = (id, body = {}) =>
  authFetch(`/admin/pulse/cards/${encodeURIComponent(id)}/regenerate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/**
 * POST /admin/pulse/cards/:id/hero-crop — Fan Story only.
 * Recrops the original to 4:3 around a 0–1 focus point. Response matches GET card.
 */
export const cropPulseCardHero = (id, body) =>
  authFetch(`/admin/pulse/cards/${encodeURIComponent(id)}/hero-crop`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

/** GET /admin/pulse/actors — support: who is hitting Pulse */
export const listPulseActors = (filters = {}) => {
  const params = new URLSearchParams();
  const limit = Math.min(100, Math.max(1, Number(filters.limit) || 40));
  params.set('limit', String(limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  if (filters.search) params.set('search', filters.search);
  return authFetch(`/admin/pulse/actors?${params.toString()}`, { method: 'GET' });
};

/**
 * GET /admin/pulse/actors/order — live card order BE would serve this actor.
 * Pass key=pulse:… or user_id / installation_id / guest_hash.
 */
export const getPulseActorOrder = (filters = {}) => {
  const params = new URLSearchParams();
  const limit = Math.min(100, Math.max(1, Number(filters.limit) || 50));
  params.set('limit', String(limit));
  if (filters.offset != null) params.set('offset', String(filters.offset));
  if (filters.key) params.set('key', filters.key);
  if (filters.user_id != null && filters.user_id !== '') {
    params.set('user_id', String(filters.user_id));
  }
  if (filters.installation_id) params.set('installation_id', filters.installation_id);
  if (filters.guest_hash) params.set('guest_hash', filters.guest_hash);
  return authFetch(`/admin/pulse/actors/order?${params.toString()}`, { method: 'GET' });
};
